/**
 * Stable content hashing for regeneration caching.
 *
 * A feature's evaluation is a pure function of (type, params, suppressed,
 * hashes of its dependencies' results). Hashing that tuple gives a cache key:
 * when the user edits feature #2 of 40, features whose transitive inputs are
 * unchanged hit the cache and only the true downstream cone re-executes.
 *
 * FNV-1a over a canonical JSON encoding. Not cryptographic — it only needs to
 * be stable and well-distributed for cache keying within one document.
 */

/** JSON.stringify with sorted object keys so hashing is order-independent. */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(value, (_key, v) => {
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      const sorted: Record<string, unknown> = {};
      for (const k of Object.keys(v).sort()) sorted[k] = (v as Record<string, unknown>)[k];
      return sorted;
    }
    return v;
  });
}

/** One 32-bit FNV-1a pass from a given seed. */
function fnv1a32(input: string, seed: number): number {
  let h = seed >>> 0;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    // h *= 16777619 (FNV prime), via shifts to stay in 32-bit int math.
    h = (h + (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24)) >>> 0;
  }
  return h >>> 0;
}

/** Two independent 32-bit FNV-1a passes concatenated → 16-char hex string. */
export function fnv1a64(input: string): string {
  const a = fnv1a32(input, 0x811c9dc5);
  const b = fnv1a32(input, 0x01000193);
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}

export function hashValue(value: unknown): string {
  return fnv1a64(canonicalJson(value));
}
